/// <amd-module name="VSS/Features/Frame/ContributedPivotBarActionProvider" />

import { IVssPageContext } from "VSS/Platform/Context";
import { IContributedCommand, IVssContributionService, IContributedService } from "VSS/Platform/Contribution";
import { IPivotBarAction, IVssPivotBarActionProvider } from "VSSUI/Components/PivotBar/PivotBarAction.Props";
import { IVssIconProps } from "VSSUI/Components/VssIcon/VssIcon.Props";
import { IUserAction } from "VSSUI/Utilities/IUserAction";
import { contributionIconToIconProps } from "./ContributedIconUtil";
import { IVssContributablePivotBarActionService } from "./IVssContributablePivotBarActionService";
import { IVssContributableActionService } from "IVssContributableActionService";
import { IContributedMenuItem } from "./IContributedMenuItem";
import { IContributedIconDefinition } from "VSS/Platform/ContributionTypes";

interface IPivotBarActionWithCommand extends IPivotBarAction {
    command?: IContributedCommand;
    registeredObjectId?: string;
    uri?: string;
}

interface IContributedAction {
    text: string;
    title?: string;
    command?: IContributedCommand;
    iconProps?: IVssIconProps;
    icon?: string | IContributedIconDefinition;
    important?: boolean;
    registeredObjectId?: string;
    uri?: string;
}

export type PivotActionContextType = (action: IPivotBarAction, isProvider?: boolean) => {};

export interface IContributedPivotBarActionProviderOptions {
    /** page context */
    context: IVssPageContext;
    /** Find actions that target this id. */
    targetId: string;
    /** Contribution type to search for (defaults to ms.vss-web.action if not specified) */
    contributionType?: string;

    getContext?: PivotActionContextType;
}

/**
 * Provides contributed actions to a PivotBar.
 */
export class ContributedPivotBarActionProvider implements IVssPivotBarActionProvider {
    private _pageContext: IVssPageContext;
    private _targetId: string;
    private _contributionType: string;
    private _getContext?: PivotActionContextType;

    public constructor(options: IContributedPivotBarActionProviderOptions) {
        this._pageContext = options.context;
        this._targetId = options.targetId;
        this._contributionType = options.contributionType || "ms.vss-web.action";
        this._getContext = options.getContext;
    }

    public loadItems(itemsUpdated: (items: IPivotBarAction[], itemsRemoved?: string[]) => void): void {
        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        const children = contributionService.getContributionChildren(this._targetId, this._contributionType);
        const actions: IPivotBarAction[] = [];

        if (children) {
            for (const childId of children) {
                // look up the contribution for the child
                const contribution = contributionService.getContribution<IContributedAction>(childId);

                if (contribution) {
                    const action: IPivotBarActionWithCommand = {
                        key: childId,
                        name: contribution.text ? contribution.text : contribution.title,
                        iconProps: contribution.iconProps || contributionIconToIconProps(this._pageContext, childId, contribution.icon),
                        important: contribution.important,
                        command: contribution.command,
                        registeredObjectId: contribution.registeredObjectId,
                        uri: contribution.uri,
                        onClick: (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, userAction?: IUserAction): void => {
                            if (action) {
                                let context: {} | undefined;
                                if (this._getContext) {
                                    context = this._getContext(action);
                                }

                                if (action.command) {
                                    contributionService.executeCommandEx(childId, action.command, context);
                                } else {
                                    const contributedService: IContributedService = {
                                        serviceName: action.registeredObjectId || action.key,
                                        uri: action.uri,
                                        configuration: context
                                    };

                                    contributionService
                                        .getServiceEx<IVssContributableActionService>(action.key, contributedService, true)
                                        .then(contributedActionService => {
                                            if (contributedActionService && contributedActionService.execute) {
                                                contributedActionService.execute(context);
                                            }
                                        });
                                }
                            }
                        }
                    };
                    actions.push(action);
                }
            }
        }

        itemsUpdated(actions);

        let allProviderIds: string[] = contributionService.getContributionChildren(this._targetId, "ms.vss-web.action-provider") || [];

        allProviderIds.forEach(providerId => {
            contributionService
                .getService<IVssContributablePivotBarActionService>(providerId, true)
                .then((actionContributionService: IVssContributablePivotBarActionService | undefined) => {
                    if (actionContributionService) {
                        const context = this._getContext
                            ? this._getContext(
                                  {
                                      key: providerId
                                  } as IPivotBarActionWithCommand,
                                  true
                              )
                            : undefined;

                        if (actionContributionService.loadItems) {
                            actionContributionService.loadItems((items: IPivotBarAction[], removedItems?: string[]): void => {
                                itemsUpdated(items, removedItems);
                            }, context);
                        } else if (actionContributionService.getMenuItems) {
                            // Convert contributed menu items to pivot bar actions
                            const processItems = (items: IContributedMenuItem[]) => {
                                const processedItems: IPivotBarAction[] = [];
                                for (let i = 0; i < items.length; i++) {
                                    const item = items[i];
                                    let pivotAction = this._convertContributedMenuToPivotAction(item, providerId, i.toString());
                                    processedItems.push(pivotAction);
                                }

                                return processedItems;
                            };

                            const items = actionContributionService.getMenuItems(context || {});
                            if (items instanceof Promise) {
                                items.then((pivotitems: IContributedMenuItem[]) => {
                                    itemsUpdated(processItems(pivotitems), []);
                                });
                            } else {
                                itemsUpdated(processItems(items), []);
                            }
                        }
                    }
                });
        });
    }

    private _convertContributedMenuToPivotAction(item: IContributedMenuItem, providerId: string, identifier: string): IPivotBarActionWithCommand {
        let pivotAction: IPivotBarActionWithCommand = {
            key: providerId + identifier,
            name: item.text ? item.text : item.title,
            iconProps: contributionIconToIconProps(this._pageContext, this._targetId, item.icon),
            separator: item.separator,
            disabled: item.disabled,
            href: item.href,
            onClick: (
                ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                userAction?: IUserAction
            ): void => {
                if (item.action) {
                    const actionContext = this._getContext
                        ? this._getContext(
                              {
                                  key: providerId
                              } as IPivotBarActionWithCommand,
                              true
                          )
                        : undefined;

                    item.action(actionContext);
                }
            }
        };

        if (item.childItems && item.childItems instanceof Array) {
            let childItems: IPivotBarAction[] = [];
            item.childItems.forEach((childMenu: IContributedMenuItem, j: number) => {
                childItems.push(this._convertContributedMenuToPivotAction(childMenu, providerId, identifier + "." + j.toString()));
            });
            pivotAction.children = childItems;
        }

        return pivotAction;
    }
}
